import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import { Calendar } from 'primereact/calendar'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { classNames } from 'primereact/utils'

import { PanelContainer } from '../../../../components/Panel'
import { StatusTag } from '../../../../components/tableTemplates'
import { usePanelContext } from '../../../../contexts/panel'
import { useToastContext } from '../../../../contexts/toast'
import { getHandleableError } from '../../../../utils/errors/isXiaError'
import { getConfigTranslation } from '../../../../utils/helper/getConfigTranslation'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../utils/helper/validateFormikFields'
import i18n from '../../../../utils/i18n'
import { ConfigurableComponent } from '../../../../utils/wrapper'
import { useConfigContext } from '../../../ui-config/context'
import { InvoiceDto, InvoicePatchDto } from '../../data/model'

type Props = {
  data: InvoiceDto
  onSubmit: (data: InvoicePatchDto) => Promise<void>
  isLoading: boolean
}

interface FormValues extends InvoiceDto {}
type FormFieldName = keyof InvoiceDto

export const InvoiceData: React.FC<Props> = ({ data, onSubmit, isLoading }) => {
  const [isEnableErrorMsg, setIsEnableErrorMsg] = useState<boolean>(false)
  const { t } = useTranslation()
  const { language } = i18n

  const handleSubmit =
    onSubmit ||
    (async () => {
      return
    })

  const panelId = 'invoice-invoiceData'
  const { isEdit } = usePanelContext()

  const { showToast } = useToastContext()
  const { uiConfig: config } = useConfigContext()

  // formik
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: { ...data },
    validate: data => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'invoiceNumber',
          config: config.invoice.invoiceData.invoiceNumber,
        },
        {
          fieldName: 'department',
          config: config.invoice.invoiceData.department,
        },
        {
          fieldName: 'name',
          config: config.invoice.invoiceData.name,
        },
        {
          fieldName: 'phone',
          config: config.invoice.invoiceData.phone,
        },
        {
          fieldName: 'email',
          config: config.invoice.invoiceData.email,
        },
        {
          fieldName: 'notes',
          config: config.invoice.invoiceData.notes,
        },
      ]

      validateFormikFields(fieldsToValidate, data, errors)

      return errors
    },
    onSubmit: data => {
      try {
        // send data in DespatchAdviceData
        handleSubmit(data)

        // show success toast
        showToast({
          style: {},
          closable: true,
          severity: 'success',
          summary: t('invoice.invoiceData.save.messages.success.summary'),
          detail: t('invoice.invoiceData.save.messages.success.detail'),
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: t('invoice.invoiceData.save.messages.failure.summary'),
          detail: errorMessage,
        })
      }
    },
  })

  const showError = (name: FormFieldName) => {
    const hasEmailError = formik.errors.email !== undefined
    const hasInvoiceNumberError = formik.errors.invoiceNumber !== undefined
    const hasPhoneError = formik.errors.phone !== undefined

    return hasEmailError ||
      hasInvoiceNumberError ||
      hasPhoneError ||
      (hasEmailError && hasInvoiceNumberError && hasPhoneError)
      ? !!formik.errors[name]
      : !!(formik.touched[name] && formik.errors[name])
  }

  const isFormFieldValid = (name: FormFieldName) => showError(name)
  const getFormErrorMessage = (name: FormFieldName) => {
    return (
      isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }
  useEffect(() => {
    if (
      formik.errors.invoiceNumber !== undefined ||
      formik.errors.email !== undefined ||
      formik.errors.phone !== undefined
    ) {
      setIsEnableErrorMsg(true)
    } else {
      setIsEnableErrorMsg(false)
    }
  }, [
    isEnableErrorMsg,
    formik.errors.invoiceNumber,
    formik.errors.email,
    formik.errors.phone,
  ])

  const movedComponentIfErrorMsgEnable = (msgIdText: any) => {
    return isEnableErrorMsg ? (
      <div style={{ minHeight: '2.5em' }}>{getFormErrorMessage(msgIdText)}</div>
    ) : (
      <div>{getFormErrorMessage(msgIdText)}</div>
    )
  }

  // ************************************************************
  // components
  // ************************************************************
  // invoiceNumber
  const invoiceNumberLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('invoiceNumber'),
      })}
      htmlFor="invoiceNumber"
    ></label>
  )

  const invoiceNumberComponent = (
    <InputText
      id="invoiceNumber"
      value={formik.values.invoiceNumber}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('invoiceNumber'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const invoiceNumberErrorMsg = movedComponentIfErrorMsgEnable('invoiceNumber')

  const invoiceNumberWrapper = (
    <ConfigurableComponent
      config={config.invoice.invoiceData}
      component={invoiceNumberComponent}
      label={invoiceNumberLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={invoiceNumberErrorMsg}
      isLoading={isLoading}
    />
  )

  // deliveryType
  const deliveryTypeLabel = (
    <label className="text-sm" htmlFor="deliveryType"></label>
  )

  const deliveryTypeComponent = (
    <InputText
      id="deliveryType"
      value={
        data.deliveryType === 'FULL'
          ? t('common.codes.deliveryType.full')
          : t('common.codes.deliveryType.partly')
      }
      className="w-full"
      disabled
    />
  )
  const deliveryTypeErrorMsg = movedComponentIfErrorMsgEnable('deliveryType')

  const deliveryTypeWrapper = (
    <ConfigurableComponent
      config={config.invoice.invoiceData}
      component={deliveryTypeComponent}
      label={deliveryTypeLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={deliveryTypeErrorMsg}
      isLoading={isLoading}
    />
  )

  // confirmationCode
  const confirmationCodeLabel = (
    <label className="text-sm w-full" htmlFor="confirmationCode"></label>
  )

  const confirmationCodeComponent = (
    <StatusTag
      id="confirmationCode"
      status={data.confirmationCode}
      className="p-2"
    />
  )
  const confirmationCodeErrorMsg =
    movedComponentIfErrorMsgEnable('confirmationCode')

  const confirmationCodeWrapper = (
    <ConfigurableComponent
      config={config.invoice.invoiceData}
      component={confirmationCodeComponent}
      label={confirmationCodeLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3 xl:col-offset-3"
      errorMsg={confirmationCodeErrorMsg}
      isLoading={isLoading}
    />
  )

  // confirmationId
  const confirmationIdLabel = (
    <label className="text-sm" htmlFor="confirmationId"></label>
  )

  const confirmationIdComponent = (
    <InputText
      id="confirmationId"
      value={data.confirmationId}
      className="w-full"
      disabled
    />
  )

  const confirmationIdErrorMsg =
    movedComponentIfErrorMsgEnable('confirmationId')

  const confirmationIdWrapper = (
    <ConfigurableComponent
      config={config.invoice.invoiceData}
      component={confirmationIdComponent}
      label={confirmationIdLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={confirmationIdErrorMsg}
      isLoading={isLoading}
    />
  )

  // confirmationDate
  const confirmationDateLabel = (
    <label className="text-sm" htmlFor="confirmationDate"></label>
  )

  const confirmationDateComponent = (
    <Calendar
      id="confirmationDate"
      value={new Date(data.confirmationDate!)}
      className="w-full"
      readOnlyInput
      showIcon
      locale={language}
      disabled
    />
  )

  const confirmationDateErrorMsg =
    movedComponentIfErrorMsgEnable('confirmationDate')

  const confirmationDateWrapper = (
    <ConfigurableComponent
      config={config.invoice.invoiceData}
      component={confirmationDateComponent}
      label={confirmationDateLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={confirmationDateErrorMsg}
      isLoading={isLoading}
    />
  )

  // deliveryNoteNumber
  const deliveryNoteNumberLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('deliveryNoteNumber'),
      })}
      htmlFor="deliveryNoteNumber"
    ></label>
  )

  const deliveryNoteNumberComponent = (
    <InputText
      id="deliveryNoteNumber"
      value={data.deliveryNoteNumber}
      className="w-full"
      disabled
    />
  )
  const deliveryNoteNumberErrorMsg =
    movedComponentIfErrorMsgEnable('deliveryNoteNumber')

  const deliveryNoteNumberWrapper = (
    <ConfigurableComponent
      config={config.invoice.invoiceData}
      component={deliveryNoteNumberComponent}
      label={deliveryNoteNumberLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={deliveryNoteNumberErrorMsg}
      isLoading={isLoading}
    />
  )

  // deliveryNoteDate
  const deliveryNoteDateLabel = (
    <label className="text-sm" htmlFor="deliveryNoteDate"></label>
  )

  const deliveryNoteDateComponent = (
    <Calendar
      id="deliveryNoteDate"
      value={new Date(data.deliveryNoteDate!)}
      className="w-full"
      readOnlyInput
      showIcon
      locale={language}
      disabled
    />
  )

  const deliveryNoteDateErrorMsg =
    movedComponentIfErrorMsgEnable('deliveryNoteDate')

  const deliveryNoteDateWrapper = (
    <ConfigurableComponent
      config={config.invoice.invoiceData}
      component={deliveryNoteDateComponent}
      label={deliveryNoteDateLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={deliveryNoteDateErrorMsg}
      isLoading={isLoading}
    />
  )

  // department
  const departmentLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('department'),
      })}
      htmlFor="department"
    ></label>
  )

  const departmentComponent = (
    <InputText
      id="department"
      value={formik.values.department}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('department'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const departmentErrorMsg = movedComponentIfErrorMsgEnable('department')

  const departmentWrapper = (
    <ConfigurableComponent
      config={config.invoice.invoiceData}
      component={departmentComponent}
      label={departmentLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={departmentErrorMsg}
      isLoading={isLoading}
    />
  )

  // name
  const nameLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('name'),
      })}
      htmlFor="name"
    ></label>
  )

  const nameComponent = (
    <InputText
      id="name"
      value={formik.values.name}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('name'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const nameErrorMsg = movedComponentIfErrorMsgEnable('name')

  const nameWrapper = (
    <ConfigurableComponent
      config={config.invoice.invoiceData}
      component={nameComponent}
      label={nameLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={nameErrorMsg}
      isLoading={isLoading}
    />
  )

  // phone
  const phoneLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('phone'),
      })}
      htmlFor="phone"
    ></label>
  )

  const phoneComponent = (
    <InputText
      id="phone"
      value={formik.values.phone}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('phone'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const phoneErrorMsg = movedComponentIfErrorMsgEnable('phone')

  const phoneWrapper = (
    <ConfigurableComponent
      config={config.invoice.invoiceData}
      component={phoneComponent}
      label={phoneLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={phoneErrorMsg}
      isLoading={isLoading}
    />
  )

  // email
  const emailLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('email'),
      })}
      htmlFor="email"
    ></label>
  )

  const emailComponent = (
    <InputText
      id="email"
      value={formik.values.email}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('email'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const emailErrorMsg = movedComponentIfErrorMsgEnable('email')

  const emailWrapper = (
    <ConfigurableComponent
      config={config.invoice.invoiceData}
      component={emailComponent}
      label={emailLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={emailErrorMsg}
      isLoading={isLoading}
    />
  )

  // notes
  const notesLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('notes'),
      })}
      htmlFor="notes"
    ></label>
  )

  const notesComponent = (
    <InputTextarea
      id="notes"
      value={formik.values.notes}
      onChange={formik.handleChange}
      rows={5}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('notes'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const notesErrorMsg = movedComponentIfErrorMsgEnable('notes')

  const notesWrapper = (
    <ConfigurableComponent
      config={config.invoice.invoiceData}
      component={notesComponent}
      label={notesLabel}
      className="col-12"
      errorMsg={notesErrorMsg}
      isLoading={isLoading}
    />
  )

  return (
    <PanelContainer
      formik={formik}
      header={getConfigTranslation(config.invoice.invoiceData.title)}
      id={panelId}
      visible={config.invoice.invoiceData.enabled}
    >
      <form onSubmit={formik.handleSubmit}>
        <div className="formgrid grid">
          {/* invoiceNumber */}
          {invoiceNumberWrapper}

          {/* deliveryType */}
          {deliveryTypeWrapper}

          {/* confirmationCode */}
          {confirmationCodeWrapper}

          {/* confirmationId */}
          {confirmationIdWrapper}

          {/* confirmationDate */}
          {confirmationDateWrapper}

          {/* deliveryNoteNumber */}
          {deliveryNoteNumberWrapper}

          {/* deliveryNoteDate */}
          {deliveryNoteDateWrapper}

          {/* department */}
          {departmentWrapper}

          {/* name */}
          {nameWrapper}

          {/* phone */}
          {phoneWrapper}

          {/* email */}
          {emailWrapper}

          {/* notes */}
          {notesWrapper}
        </div>
      </form>
    </PanelContainer>
  )
}
